/**
 * About timeline / testimonial slider (Webflow `.slider.w-slider`).
 *
 * Rebuilt as a React + GSAP slider (no Webflow JS, no library): 3 slides,
 * cross-fade transition (1700ms), infinite loop, custom prev/next arrows. The
 * nav dots are present but hidden, matching the export.
 *
 * Per-slide content motion mirrors the Webflow IX2 lists a-33 (SLIDER_ACTIVE,
 * enter) and a-34 (SLIDER_INACTIVE, leave) on `.slider-text`,
 * `.slider-description`, `.slider-image` and `.year-text` — see ANIMATIONS.md
 * §12a for the exact durations/easings.
 *
 * The Webflow base CSS makes `.slider/.slide/.mask` position:static, so slides
 * are stacked here with runtime-computed absolute positioning (allowed) and the
 * active slide drives the holder height.
 */
import type { FC } from 'react'

import { useEffect, useRef, useState } from 'react'

import { gsap } from '@/lib/gsap'

interface Slide {
    year: string
    yearSmall: string
    image: string
    srcSet: string
    title: string
    description: string
}

const SLIDES: Slide[] = [
    {
        year: '1972',
        yearSmall: '1973',
        image: '/images/CTA-Image-01.jpg',
        srcSet: '/images/CTA-Image-01-p-500.jpg 500w, /images/CTA-Image-01-p-800.jpg 800w, /images/CTA-Image-01-p-1080.jpg 1080w, /images/CTA-Image-01.jpg 1300w',
        title: 'In the business since 1972',
        description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Quis nunc, netus faucibus adipiscing. Posuere nisl massa interdum magna. Sollicitudin ac enim libero id ac. Aenean pellentesque in eu dolor, tristique viverra amet bibendum.'
    },
    {
        year: '1973',
        yearSmall: '1972',
        image: '/images/Contact-Hero-Image-1.jpg',
        srcSet: '/images/Contact-Hero-Image-1-p-500.jpg 500w, /images/Contact-Hero-Image-1-p-800.jpg 800w, /images/Contact-Hero-Image-1.jpg 1270w',
        title: 'In the business since 1972',
        description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Quis nunc, netus faucibus adipiscing. Posuere nisl massa interdum magna. Sollicitudin ac enim libero id ac. Aenean pellentesque in eu dolor, tristique viverra amet bibendum.'
    },
    {
        year: '1984',
        yearSmall: '1984',
        image: '/images/CTA-Image-06.jpg',
        srcSet: '/images/CTA-Image-06-p-500.jpg 500w, /images/CTA-Image-06.jpg 690w',
        title: 'In the business since 1984',
        description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Quis nunc, netus faucibus adipiscing. Posuere nisl massa interdum magna. Sollicitudin ac enim libero id ac. Aenean pellentesque in eu dolor, tristique viverra amet bibendum.'
    }
]

const IMAGE_SIZES = '(max-width: 479px) 150px, (max-width: 991px) 270px, 370px'

export const AboutSlider: FC = () => {
    const sliderRef = useRef<HTMLDivElement>(null)
    const slideRefs = useRef<Array<HTMLDivElement | null>>([])
    const [active, setActive] = useState(0)
    const animating = useRef(false)

    // Enter (a-33): bring the active slide's content to rest.
    const enter = (el: HTMLElement) => {
        gsap.to(el.querySelectorAll('.slider-text, .slider-description'), {
            yPercent: 0,
            opacity: 1,
            duration: 0.8,
            ease: 'power1.inOut'
        })
        gsap.to(el.querySelector('.slider-image'), {
            xPercent: 0,
            yPercent: 0,
            opacity: 1,
            duration: 0.8,
            ease: 'power1.inOut'
        })
        gsap.to(el.querySelector('.year-text'), {
            yPercent: 0,
            duration: 0.8,
            ease: 'power1.inOut'
        })
    }

    // Leave (a-34): push the previous slide's content out.
    const leave = (el: HTMLElement) => {
        gsap.to(el.querySelectorAll('.slider-text, .slider-description'), {
            yPercent: -140,
            opacity: 0,
            duration: 0.7,
            ease: 'power1.inOut'
        })
        gsap.to(el.querySelector('.slider-image'), {
            xPercent: -100,
            opacity: 0,
            duration: 0.7,
            ease: 'power1.inOut'
        })
        gsap.to(el.querySelector('.year-text'), {
            yPercent: -160,
            duration: 0.7,
            ease: 'power1.inOut'
        })
    }

    // Initial stacking + hidden state for every slide except the first.
    useEffect(() => {
        const slides = slideRefs.current.filter(Boolean) as HTMLDivElement[]
        if (!slides.length) return

        const ctx = gsap.context(() => {
            slides.forEach((slide, index) => {
                gsap.set(slide, {
                    position: index === 0 ? 'relative' : 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    opacity: index === 0 ? 1 : 0,
                    zIndex: index === 0 ? 2 : 1
                })

                if (index !== 0) {
                    gsap.set(slide.querySelectorAll('.slider-text, .slider-description'), {
                        yPercent: -140,
                        opacity: 0
                    })
                    gsap.set(slide.querySelector('.slider-image'), { xPercent: -100, opacity: 0 })
                    gsap.set(slide.querySelector('.year-text'), { yPercent: -160 })
                }
            })
        }, sliderRef)

        return () => ctx.revert()
    }, [])

    const goTo = (next: number) => {
        const slides = slideRefs.current.filter(Boolean) as HTMLDivElement[]
        const total = slides.length
        if (!total || animating.current) return

        const target = ((next % total) + total) % total
        if (target === active) return

        const current = slides[active]
        const incoming = slides[target]
        animating.current = true

        // Stack: keep current relative (holds height) until the cross-fade ends;
        // bring the incoming slide over it as an absolute overlay.
        gsap.set(incoming, { position: 'absolute', zIndex: 3, top: 0, left: 0, width: '100%' })
        gsap.to(current, { opacity: 0, duration: 1.7, ease: 'power1.inOut' })
        gsap.to(incoming, {
            opacity: 1,
            duration: 1.7,
            ease: 'power1.inOut',
            onComplete: () => {
                gsap.set(incoming, { position: 'relative', zIndex: 2 })
                gsap.set(current, { position: 'absolute', zIndex: 1, opacity: 0 })
                animating.current = false
            }
        })

        leave(current)
        enter(incoming)
        setActive(target)
    }

    return (
        <div
            ref={sliderRef}
            data-delay='4000'
            data-animation='cross'
            className='slider w-slider'
            data-autoplay='false'
            data-easing='ease'
            data-hide-arrows='false'
            data-disable-swipe='false'
            data-autoplay-limit='0'
            data-nav-spacing='3'
            data-duration='1700'
            data-infinite='true'
        >
            <div className='mask w-slider-mask'>
                {SLIDES.map((slide, index) => (
                    <div
                        key={index}
                        ref={(node) => {
                            slideRefs.current[index] = node
                        }}
                        className='slide w-slide'
                    >
                        <div className='about-slider-holder'>
                            <div className='slider-image-holder'>
                                <div className='slider-number-container'>
                                    <h1 className='year-text'>{slide.year}</h1>
                                </div>
                                <img
                                    src={slide.image}
                                    loading='lazy'
                                    srcSet={slide.srcSet}
                                    alt=''
                                    sizes={IMAGE_SIZES}
                                    className='slider-image'
                                />
                            </div>
                            <div className='slider-details-container'>
                                <h2 className='slider-text'>{slide.title}</h2>
                                <p className='slider-description'>
                                    {slide.description}
                                    <br />
                                </p>
                            </div>
                            <div className='slider-lines-container'>
                                <div className='slider-line' />
                                <div className='slider-line' />
                                <div className='slider-year-small'>{slide.yearSmall}</div>
                                <div className='slider-line' />
                                <div className='slider-line' />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <div
                className='about-slider-left-arrow w-slider-arrow-left'
                role='button'
                tabIndex={0}
                onClick={() => goTo(active - 1)}
            >
                <img src='/images/Slider-Arrow-DOwn.svg' loading='lazy' alt='' className='arrow-up-image' />
            </div>
            <div
                className='right-arrow w-slider-arrow-right'
                role='button'
                tabIndex={0}
                onClick={() => goTo(active + 1)}
            >
                <img src='/images/Slider-Arrow-Up.svg' loading='lazy' alt='' className='arrow-up-image' />
            </div>
            <div className='hide w-slider-nav w-round' />
        </div>
    )
}
